import "zod/compile";
import { z } from "zod";
import {
  AR_CASH_METRICS_CHECK_LAG_BUCKETS,
  AR_CASH_METRICS_DATE_GAP_BUCKETS,
  AR_CASH_METRICS_FILING_PAYMENT_TYPES,
  ArCashMetricsCheckCustomerSchema,
  ArCashMetricsEscalationSchema,
  ArCashMetricsMarkedReceiptSchema,
  ArCashMetricsMissedEmailSchema,
} from "./ar-cash-metrics.contract";
import { CUSTOMER_REMITTANCE_TYPES } from "./ar-customer-profile.contract";

/**
 * The AR cash query grammar: the one fixed, server-side vocabulary the Cash
 * Dashboard widgets and Nami ask cash questions in. An aggregate query is
 * measures × dimensions × filters over a range; a list query names one of a
 * few row lists. Nothing outside this catalog can be asked: an unknown name
 * or an unsupported combination is a typed error, never an approximation.
 *
 * Every measure, dimension and list carries its own definition text (label,
 * description, unit), so a widget's description and Nami's tool description
 * read from the same place and cannot drift from what the server computes.
 *
 * The definitions are the static cash metrics page's (`arCashMetrics.get`):
 * Pacific calendar days, Famous receipts dated in the range, keyed by Gently
 * when the description carries the agent's "entry by Gently" stamp, check
 * date from Famous REFDATE, and the bank feed (Famous and Plaid only) as the
 * cutoff for whether a remittance could have matched yet.
 */

/** A calendar day, YYYY-MM-DD: the shape alone would let 2026-02-30 through. */
export const CashDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD")
  .refine((day) => {
    const ms = Date.parse(`${day}T00:00:00Z`);
    return (
      Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === day
    );
  }, "Not a calendar day");
const DaySchema = CashDaySchema;

/** Longest range a query may cover, in days: a year, leap years included. */
export const AR_CASH_QUERY_MAX_RANGE_DAYS = 366;
/** Most queries in one batched request. */
export const AR_CASH_QUERY_MAX_BATCH = 50;
export const AR_CASH_QUERY_MAX_LIST_LIMIT = 50;
export const AR_CASH_QUERY_DEFAULT_LIST_LIMIT = 10;
export const AR_CASH_QUERY_MAX_MEASURES = 20;
export const AR_CASH_QUERY_MAX_DIMENSIONS = 3;

function dayNumber(day: string): number {
  return Date.parse(`${day}T00:00:00Z`) / 86_400_000;
}

/** Inclusive number of days from `from` to `to` (both YYYY-MM-DD). */
export function cashRangeDayCount(from: string, to: string): number {
  return dayNumber(to) - dayNumber(from) + 1;
}

// ── Range ──────────────────────────────────────────────────────────────────

/**
 * Pacific calendar days. `last_days` ends today (Pacific) and counts today,
 * like the static page's 7d / 14d / 30d. `between` is inclusive on both ends.
 */
export const CashRangeSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("last_days"),
    days: z.int().min(1).max(AR_CASH_QUERY_MAX_RANGE_DAYS),
  }),
  z
    .object({ kind: z.literal("between"), from: DaySchema, to: DaySchema })
    .refine((range) => range.from <= range.to, {
      message: "`from` must be on or before `to`",
      path: ["to"],
    })
    .refine(
      (range) =>
        cashRangeDayCount(range.from, range.to) <= AR_CASH_QUERY_MAX_RANGE_DAYS,
      {
        message: `A range covers at most ${AR_CASH_QUERY_MAX_RANGE_DAYS} days`,
        path: ["from"],
      },
    ),
]);
export type CashRange = z.infer<typeof CashRangeSchema>;

/** The days a range resolved to, both inclusive, Pacific. */
export const CashResolvedRangeSchema = z.object({
  from: DaySchema,
  to: DaySchema,
});
export type CashResolvedRange = z.infer<typeof CashResolvedRangeSchema>;

// ── Units ──────────────────────────────────────────────────────────────────

export const CASH_UNITS = ["count", "money", "ratio", "days"] as const;
export type CashUnit = (typeof CASH_UNITS)[number];

export interface CashUnitDefinition {
  label: string;
  description: string;
  /** d3-format specifier, for Vega axes and tooltips. */
  vegaFormat: string;
}

/**
 * How each unit's values arrive: counts as integers, money as decimal strings
 * ("1234.56", USD), ratios as a fraction from 0 to 1 or null when the
 * denominator is zero, days as a number or null when there is nothing to
 * measure.
 */
export const CASH_UNIT_DEFINITIONS = {
  count: {
    label: "Count",
    description: "A whole number.",
    vegaFormat: ",d",
  },
  money: {
    label: "US dollars",
    description: 'A decimal string in US dollars, e.g. "1234.56".',
    vegaFormat: "$,.2f",
  },
  ratio: {
    label: "Percent",
    description:
      "A fraction from 0 to 1, shown as a percent; null when nothing is in the denominator.",
    vegaFormat: ".0%",
  },
  days: {
    label: "Days",
    description: "A number of days; null when nothing was measured.",
    vegaFormat: ",.1f",
  },
} as const satisfies Record<CashUnit, CashUnitDefinition>;

// ── Dimensions ─────────────────────────────────────────────────────────────

export const CASH_DIMENSIONS = [
  "day",
  "week",
  "month",
  "bank",
  "payment_type",
  "keyed_by",
  "customer",
  "remit_type",
  "remittance_status",
  "check_lag_bucket",
  "date_gap_bucket",
] as const;
export const CashDimensionSchema = z.enum(CASH_DIMENSIONS);
export type CashDimension = z.infer<typeof CashDimensionSchema>;

export const CASH_TIME_DIMENSIONS = [
  "day",
  "week",
  "month",
] as const satisfies readonly CashDimension[];

export const CASH_KEYED_BY_VALUES = ["gently", "team"] as const;
export const CASH_REMITTANCE_STATUSES = [
  "linked",
  "unmatched",
  "no_customer",
  "awaiting_bank",
] as const;

export interface CashDimensionDefinition {
  label: string;
  description: string;
  /**
   * The full set of values, for dimensions with a fixed set. A query with
   * just this one dimension returns a row for every value, zeros included.
   * Time dimensions return a row for every day, week or month of the range.
   */
  values?: readonly (string | null)[];
  /** Labels for the fixed values, keyed by value ("null" for null). */
  valueLabels?: Readonly<Record<string, string>>;
}

/**
 * Each dimension becomes a column of the result rows, named by its id. A
 * null value means "not recorded" (no bank account name in Famous, no
 * customer on the receipt, no remit type set on the customer).
 */
export const CASH_DIMENSION_DEFINITIONS = {
  day: {
    label: "Day",
    description:
      "Pacific calendar day (YYYY-MM-DD): the receipt date for receipts, the day received for remittances.",
  },
  week: {
    label: "Week",
    description:
      "Week starting Monday, as the Monday's date (YYYY-MM-DD), Pacific. The first and last weeks can be partial when the range starts or ends midweek.",
  },
  month: {
    label: "Month",
    description:
      "Calendar month (YYYY-MM), Pacific. The first and last months can be partial.",
  },
  bank: {
    label: "Bank account",
    description:
      'Famous\'s name for the bank account the receipt was deposited to (e.g. "TRUIST BANK"); null when Famous recorded none.',
  },
  payment_type: {
    label: "Payment type",
    description:
      'Decoded Famous deposit type. Famous records ACH and wire under one code, so they share "electronic". "other" is a receipt with no deposit type in Famous (or an internal A/R adjustment).',
    values: AR_CASH_METRICS_FILING_PAYMENT_TYPES,
    valueLabels: {
      check: "Check",
      electronic: "ACH / wire",
      cash: "Cash",
      other: "Other",
    },
  },
  keyed_by: {
    label: "Keyed by",
    description:
      'Who keyed the receipt into Famous: "gently" when its description carries the agent\'s "entry by Gently" stamp, otherwise "team".',
    values: CASH_KEYED_BY_VALUES,
    valueLabels: { gently: "Gently", team: "The team" },
  },
  customer: {
    label: "Customer",
    description:
      "The receipt's customer, by id; rows also carry `customer_name`. null when the receipt has no customer.",
  },
  remit_type: {
    label: "Remit type",
    description:
      "How the receipt's customer sends remittance advice, as set on the customer profile (email, portal, edi, mail, none); null when not set.",
    values: [...CUSTOMER_REMITTANCE_TYPES, null],
    valueLabels: {
      email: "Email",
      portal: "Portal",
      edi: "EDI",
      mail: "Mail",
      none: "No remittance",
      null: "Not set",
    },
  },
  remittance_status: {
    label: "Remittance status",
    description:
      'Whether a bank receipt backs an email remittance: "linked" to one; "unmatched" although the bank feed is past its payment date; "no_customer", unmatched with no customer identified; "awaiting_bank", dated on or after the newest Famous or Plaid bank receipt, so nothing could have matched yet.',
    values: CASH_REMITTANCE_STATUSES,
    valueLabels: {
      linked: "Linked to a bank receipt",
      unmatched: "Not matched to a bank receipt",
      no_customer: "Not matched, no customer identified",
      awaiting_bank: "Bank not caught up yet",
    },
  },
  check_lag_bucket: {
    label: "Check lag",
    description:
      "Days from the date on the check (Famous REFDATE) to the receipt date. Checks dated after their receipt (typos) and checks with no check date have no bucket.",
    values: AR_CASH_METRICS_CHECK_LAG_BUCKETS,
    valueLabels: {
      same_day: "Same day",
      "1_3_days": "1–3 days",
      "4_7_days": "4–7 days",
      "8_14_days": "8–14 days",
      over_14_days: "Over 14 days",
    },
  },
  date_gap_bucket: {
    label: "Remittance date vs. bank date",
    description:
      "For a remittance linked to a bank receipt, days between the remittance's stated payment date and the bank date, either direction.",
    values: AR_CASH_METRICS_DATE_GAP_BUCKETS,
    valueLabels: {
      same_day: "Same day",
      "1_3_days": "1–3 days",
      "4_7_days": "4–7 days",
      "8_14_days": "8–14 days",
      "15_31_days": "15–31 days",
      over_31_days: "Over 31 days",
      no_stated_date: "No date on remittance",
    },
  },
} as const satisfies Record<CashDimension, CashDimensionDefinition>;

// ── Filters ────────────────────────────────────────────────────────────────

/**
 * Value lists, ANDed across keys and ORed within one. Keys are the
 * non-time dimensions; null matches "not recorded".
 */
export const CashFiltersSchema = z.strictObject({
  bank: z.array(z.string().min(1).nullable()).min(1).optional(),
  payment_type: z
    .array(z.enum(AR_CASH_METRICS_FILING_PAYMENT_TYPES))
    .min(1)
    .optional(),
  keyed_by: z.array(z.enum(CASH_KEYED_BY_VALUES)).min(1).optional(),
  customer: z.array(z.uuid().nullable()).min(1).optional(),
  remit_type: z
    .array(z.enum(CUSTOMER_REMITTANCE_TYPES).nullable())
    .min(1)
    .optional(),
  remittance_status: z
    .array(z.enum(CASH_REMITTANCE_STATUSES))
    .min(1)
    .optional(),
});
export type CashFilters = z.infer<typeof CashFiltersSchema>;
export type CashFilterKey = keyof CashFilters;
export const CASH_FILTER_KEYS = Object.keys(
  CashFiltersSchema.shape,
) as CashFilterKey[];

// ── Measures ───────────────────────────────────────────────────────────────

/**
 * What a measure is computed over. Measures from different sources can share
 * a query only when every dimension and filter applies to all of them.
 */
export const CASH_MEASURE_SOURCES = [
  "receipts",
  "remittances",
  "workflow",
] as const;
export type CashMeasureSource = (typeof CASH_MEASURE_SOURCES)[number];

const RECEIPT_DIMENSIONS = [
  "day",
  "week",
  "month",
  "bank",
  "payment_type",
  "keyed_by",
  "customer",
  "remit_type",
] as const satisfies readonly CashDimension[];
const RECEIPT_FILTERS = [
  "bank",
  "payment_type",
  "keyed_by",
  "customer",
  "remit_type",
] as const satisfies readonly CashFilterKey[];
const REMITTANCE_DIMENSIONS = [
  "day",
  "week",
  "month",
  "remittance_status",
] as const satisfies readonly CashDimension[];
const REMITTANCE_FILTERS = [
  "remittance_status",
] as const satisfies readonly CashFilterKey[];

export const CASH_MEASURES = [
  "receipt_count",
  "receipt_amount",
  "gently_keyed_count",
  "gently_keyed_amount",
  "gently_keyed_share",
  "in_scope_receipt_count",
  "in_scope_gently_keyed_count",
  "in_scope_gently_keyed_share",
  "email_remitter_receipt_count",
  "email_remitter_gently_keyed_count",
  "customer_payment_count",
  "customer_payment_amount",
  "electronic_dollar_share",
  "customers_paying_by_check",
  "customers_paying_electronically",
  "check_count",
  "checks_with_check_date",
  "checks_dated_after_receipt",
  "check_lag_count",
  "check_lag_median_days",
  "check_lag_p90_days",
  "remittance_count",
  "remittance_amount",
  "linked_remittance_count",
  "remittance_match_rate",
  "remittance_date_gap_median_days",
  "agent_tasks_attempted",
  "agent_tasks_escalated",
  "agent_tasks_keyed",
  "agent_success_rate",
  "remittance_emails_failed",
  "marked_missing_count",
  "remittances_added_manually",
  "email_remittances_missed",
  "email_remittance_capture_rate",
] as const;
export const CashMeasureSchema = z.enum(CASH_MEASURES);
export type CashMeasure = z.infer<typeof CashMeasureSchema>;

export interface CashMeasureDefinition {
  label: string;
  description: string;
  unit: CashUnit;
  source: CashMeasureSource;
  dimensions: readonly CashDimension[];
  filters: readonly CashFilterKey[];
}

function receiptMeasure(
  label: string,
  unit: CashUnit,
  description: string,
): CashMeasureDefinition {
  return {
    label,
    unit,
    description,
    source: "receipts",
    dimensions: RECEIPT_DIMENSIONS,
    filters: RECEIPT_FILTERS,
  };
}

function remittanceMeasure(
  label: string,
  unit: CashUnit,
  description: string,
): CashMeasureDefinition {
  return {
    label,
    unit,
    description,
    source: "remittances",
    dimensions: REMITTANCE_DIMENSIONS,
    filters: REMITTANCE_FILTERS,
  };
}

/** Totals over the whole range only: no dimensions or filters yet. */
function workflowMeasure(
  label: string,
  unit: CashUnit,
  description: string,
): CashMeasureDefinition {
  return {
    label,
    unit,
    description,
    source: "workflow",
    dimensions: [],
    filters: [],
  };
}

const CASH_RECEIPTS =
  "Famous cash receipts dated in the range, cash in only (reversals and zero entries are left out).";
const CUSTOMER_PAYMENTS =
  'Famous cash receipts dated in the range that are customer payments: cash in only, internal A/R adjustments left out. Receipts with no deposit type in Famous count under payment type "other".';
const IN_SCOPE =
  "Receipts Gently is set up to key: in a bank account and payment type it keyed any receipt for in the range, from customers whose remit type is email. Which pairs are in scope is decided over the whole range, before filters.";
const CHECKS = "Famous check receipts dated in the range, cash in only.";
const EMAIL_REMITTANCES = "Email remittances received in the range (Pacific).";
const AGENT_TASKS =
  "Cash receipt tasks the Famous cash receipt entry agent ran on, or escalated, in the range. Evaluation and mock runs are left out.";

export const CASH_MEASURE_DEFINITIONS = {
  receipt_count: receiptMeasure("Cash receipts", "count", CASH_RECEIPTS),
  receipt_amount: receiptMeasure(
    "Cash receipt dollars",
    "money",
    `${CASH_RECEIPTS} Their dollars.`,
  ),
  gently_keyed_count: receiptMeasure(
    "Receipts keyed by Gently",
    "count",
    `${CASH_RECEIPTS} Of those, the ones the Gently agent keyed: their description says "entry by Gently".`,
  ),
  gently_keyed_amount: receiptMeasure(
    "Dollars keyed by Gently",
    "money",
    'Dollars of the cash receipts the Gently agent keyed (description says "entry by Gently"), dated in the range.',
  ),
  gently_keyed_share: receiptMeasure(
    "Filed by Gently",
    "ratio",
    `${CASH_RECEIPTS} Of those, the share the Gently agent keyed: receipts whose description says "entry by Gently".`,
  ),
  in_scope_receipt_count: receiptMeasure(
    "Receipts in Gently's scope",
    "count",
    `${IN_SCOPE} Cash in only.`,
  ),
  in_scope_gently_keyed_count: receiptMeasure(
    "In-scope receipts keyed by Gently",
    "count",
    `${IN_SCOPE} Of those, the ones Gently keyed.`,
  ),
  in_scope_gently_keyed_share: receiptMeasure(
    "Filed by Gently, In Scope",
    "ratio",
    `${IN_SCOPE} Of those, the share Gently keyed.`,
  ),
  email_remitter_receipt_count: receiptMeasure(
    "Receipts from email remitters",
    "count",
    `${CASH_RECEIPTS} Of those, the ones from customers whose remit type is email: the receipts the agent can key from an emailed remittance.`,
  ),
  email_remitter_gently_keyed_count: receiptMeasure(
    "Email-remitter receipts keyed by Gently",
    "count",
    `${CASH_RECEIPTS} Of those from customers whose remit type is email, the ones the Gently agent keyed.`,
  ),
  customer_payment_count: receiptMeasure(
    "Customer payments",
    "count",
    CUSTOMER_PAYMENTS,
  ),
  customer_payment_amount: receiptMeasure(
    "Customer payment dollars",
    "money",
    `${CUSTOMER_PAYMENTS} Their dollars.`,
  ),
  electronic_dollar_share: receiptMeasure(
    "Paid by ACH",
    "ratio",
    "Share of check and electronic receipt dollars that came in electronically. Famous records ACH and wire under one code, so wires are included.",
  ),
  customers_paying_by_check: receiptMeasure(
    "Customers paying by check",
    "count",
    "Customers with at least one check receipt dated in the range.",
  ),
  customers_paying_electronically: receiptMeasure(
    "Customers paying by ACH or wire",
    "count",
    "Customers with at least one electronic (ACH or wire) receipt dated in the range.",
  ),
  check_count: receiptMeasure("Checks", "count", CHECKS),
  checks_with_check_date: receiptMeasure(
    "Checks with a check date",
    "count",
    `${CHECKS} Of those, the ones with the date on the check recorded in Famous (REFDATE).`,
  ),
  checks_dated_after_receipt: receiptMeasure(
    "Checks dated after their receipt",
    "count",
    "Checks whose check date is after the day their receipt was entered: almost certainly a typo in Famous, so they are left out of check lag.",
  ),
  check_lag_count: {
    ...receiptMeasure(
      "Checks with a check lag",
      "count",
      "Checks with a check date on or before their receipt date: the checks check lag is measured over. Slice by check_lag_bucket for the distribution.",
    ),
    dimensions: [...RECEIPT_DIMENSIONS, "check_lag_bucket"],
  },
  check_lag_median_days: receiptMeasure(
    "Check Lag",
    "days",
    "Median days from the date on a check to the day its receipt was entered in Famous, over the check receipts dated in the range.",
  ),
  check_lag_p90_days: receiptMeasure(
    "Check lag, 90th percentile",
    "days",
    "Days within which 90% of checks dated in the range had their receipt entered (nearest rank).",
  ),
  remittance_count: remittanceMeasure(
    "Email remittances",
    "count",
    `${EMAIL_REMITTANCES} These are the remittances Gently found.`,
  ),
  remittance_amount: remittanceMeasure(
    "Email remittance dollars",
    "money",
    `${EMAIL_REMITTANCES} Their stated totals.`,
  ),
  linked_remittance_count: {
    ...remittanceMeasure(
      "Remittances linked to a bank receipt",
      "count",
      `${EMAIL_REMITTANCES} Of those, the ones linked to a bank receipt. Slice by date_gap_bucket for how far the remittance's payment date is from the bank date; matching needs the exact amount, so dollars always agree.`,
    ),
    dimensions: [...REMITTANCE_DIMENSIONS, "date_gap_bucket"],
  },
  remittance_match_rate: remittanceMeasure(
    "Remittances matched",
    "ratio",
    `${EMAIL_REMITTANCES} The share linked to a bank receipt, leaving out the ones the bank feed hasn't caught up to yet.`,
  ),
  remittance_date_gap_median_days: remittanceMeasure(
    "Remittance vs. bank date, median",
    "days",
    "For email remittances linked to a bank receipt, median days between the remittance's stated payment date and the bank date. Remittances with no stated date are left out.",
  ),
  agent_tasks_attempted: workflowMeasure(
    "Agent tasks worked",
    "count",
    AGENT_TASKS,
  ),
  agent_tasks_escalated: workflowMeasure(
    "Agent tasks escalated",
    "count",
    `${AGENT_TASKS} Of those, tasks the agent escalated to a person (an agent_help_requested action). An escalation counts as a failure, since other failures aren't tracked yet.`,
  ),
  agent_tasks_keyed: workflowMeasure(
    "Agent tasks keyed",
    "count",
    `${AGENT_TASKS} Of those, tasks the agent itself closed as keyed.`,
  ),
  agent_success_rate: workflowMeasure(
    "Agent Success Rate",
    "ratio",
    "Cash receipt tasks the agent worked on without escalating to a person: 1 − escalated ÷ worked.",
  ),
  remittance_emails_failed: workflowMeasure(
    "Recognized but not recorded",
    "count",
    "Emails received in the range that the remittance workflow recognized as remittances (it got past classification) but finished without recording, unless another run recorded the same email.",
  ),
  marked_missing_count: workflowMeasure(
    "Receipts marked missing a remittance",
    "count",
    "Cash receipts dated in the range that an AR user marked as missing a remittance Gently should have recorded.",
  ),
  remittances_added_manually: workflowMeasure(
    "Added by hand",
    "count",
    "Remittances a person added by hand in the range, unless their receipt is marked missing in the range (already counted there).",
  ),
  email_remittances_missed: workflowMeasure(
    "Email remittances missed",
    "count",
    "Recognized but not recorded, plus receipts marked missing a remittance, plus remittances added by hand. Customers who never send a remittance are not misses.",
  ),
  email_remittance_capture_rate: workflowMeasure(
    "Email Remittances",
    "ratio",
    "Remittances Gently recorded from email, out of those plus the ones it missed.",
  ),
} as const satisfies Record<CashMeasure, CashMeasureDefinition>;

// ── Lists ──────────────────────────────────────────────────────────────────

export const CASH_LISTS = [
  "recent_escalations",
  "failed_remittance_emails",
  "receipts_marked_missing",
  "check_paying_customers",
] as const;
export const CashListSchema = z.enum(CASH_LISTS);
export type CashList = z.infer<typeof CashListSchema>;

export interface CashListDefinition {
  label: string;
  description: string;
  /** Row fields in display order, with their labels and units. */
  columns: readonly { field: string; label: string; unit?: CashUnit }[];
  filters: readonly CashFilterKey[];
}

export const CASH_LIST_DEFINITIONS = {
  recent_escalations: {
    label: "Latest escalations, with the agent's question",
    description:
      "The latest escalation of each cash receipt task the agent escalated to a person in the range, newest first.",
    columns: [
      { field: "escalatedAt", label: "Escalated" },
      { field: "customerName", label: "Customer" },
      { field: "amount", label: "Amount", unit: "money" },
      { field: "question", label: "Question" },
    ],
    filters: [],
  },
  failed_remittance_emails: {
    label: "Latest emails recognized but not recorded",
    description:
      "Emails the remittance workflow recognized as remittances but finished without recording, newest first.",
    columns: [
      { field: "receivedAt", label: "Received" },
      { field: "subject", label: "Subject" },
      { field: "sender", label: "Sender" },
    ],
    filters: [],
  },
  receipts_marked_missing: {
    label: "Latest receipts marked missing a remittance",
    description:
      "Cash receipts dated in the range that an AR user marked as missing their remittance, most recently marked first.",
    columns: [
      { field: "transactionDate", label: "Receipt date" },
      { field: "customerName", label: "Customer" },
      { field: "amount", label: "Amount", unit: "money" },
    ],
    filters: [],
  },
  check_paying_customers: {
    label: "Customers still paying by check, most check dollars first",
    description:
      "Customers with at least one Famous check receipt dated in the range, most check dollars first. Check share is checks as a % of the customer's check, electronic and cash receipts in the range.",
    columns: [
      { field: "customerName", label: "Customer" },
      { field: "check.count", label: "Checks", unit: "count" },
      { field: "check.amount", label: "Check $", unit: "money" },
      { field: "checkSharePct", label: "Check Share" },
      { field: "lastCheckDate", label: "Last Check" },
    ],
    filters: RECEIPT_FILTERS,
  },
} as const satisfies Record<CashList, CashListDefinition>;

// ── Queries ────────────────────────────────────────────────────────────────

export const CashAggregateQuerySchema = z.strictObject({
  kind: z.literal("aggregate"),
  measures: z
    .array(CashMeasureSchema)
    .min(1)
    .max(AR_CASH_QUERY_MAX_MEASURES)
    .refine((list) => new Set(list).size === list.length, {
      message: "Measures must not repeat",
    }),
  dimensions: z
    .array(CashDimensionSchema)
    .max(AR_CASH_QUERY_MAX_DIMENSIONS)
    .refine((list) => new Set(list).size === list.length, {
      message: "Dimensions must not repeat",
    })
    .prefault([]),
  filters: CashFiltersSchema.prefault({}),
  /** Absent: the page's range. */
  range: CashRangeSchema.optional(),
});
export type CashAggregateQuery = z.infer<typeof CashAggregateQuerySchema>;

export const CashListQuerySchema = z.strictObject({
  kind: z.literal("list"),
  list: CashListSchema,
  filters: CashFiltersSchema.prefault({}),
  range: CashRangeSchema.optional(),
  limit: z
    .int()
    .min(1)
    .max(AR_CASH_QUERY_MAX_LIST_LIMIT)
    .prefault(AR_CASH_QUERY_DEFAULT_LIST_LIMIT),
});
export type CashListQuery = z.infer<typeof CashListQuerySchema>;

export const CashQuerySchema = z.discriminatedUnion("kind", [
  CashAggregateQuerySchema,
  CashListQuerySchema,
]);
/** A validated query, defaults applied. */
export type CashQuery = z.infer<typeof CashQuerySchema>;
/** A query as written in a widget or by Nami, before defaults. */
export type CashQueryInput = z.input<typeof CashQuerySchema>;

// ── Results ────────────────────────────────────────────────────────────────

/**
 * One flat row per dimension combination, Vega-ready: a column per dimension
 * (by id; `customer` adds `customer_name`) and per measure (by id), valued
 * by its unit (see `CASH_UNIT_DEFINITIONS`).
 */
export const CashAggregateRowSchema = z.record(
  z.string(),
  z.union([z.number(), z.string(), z.null()]),
);
export type CashAggregateRow = z.infer<typeof CashAggregateRowSchema>;

export const CashAggregateResultSchema = z.object({
  kind: z.literal("aggregate"),
  range: CashResolvedRangeSchema,
  measures: z.array(CashMeasureSchema),
  dimensions: z.array(CashDimensionSchema),
  /**
   * Time dimensions ascending, then the first measure descending, then
   * dimension values ascending (nulls last).
   */
  rows: z.array(CashAggregateRowSchema),
});
export type CashAggregateResult = z.infer<typeof CashAggregateResultSchema>;

function listResult<L extends CashList, R extends z.ZodType>(list: L, row: R) {
  return z.object({
    kind: z.literal("list"),
    list: z.literal(list),
    range: CashResolvedRangeSchema,
    rows: z.array(row),
    /** Rows in the whole list, before the limit. */
    total: z.int().nonnegative(),
    truncated: z.boolean(),
  });
}

export const CashListResultSchema = z.discriminatedUnion("list", [
  listResult("recent_escalations", ArCashMetricsEscalationSchema),
  listResult("failed_remittance_emails", ArCashMetricsMissedEmailSchema),
  listResult("receipts_marked_missing", ArCashMetricsMarkedReceiptSchema),
  listResult("check_paying_customers", ArCashMetricsCheckCustomerSchema),
]);
export type CashListResult = z.infer<typeof CashListResultSchema>;

export const CashQueryResultSchema = z.union([
  CashAggregateResultSchema,
  CashListResultSchema,
]);
export type CashQueryResult = z.infer<typeof CashQueryResultSchema>;

/**
 * - `not_in_catalog`: names a measure, dimension, filter or list that doesn't
 *   exist ("not available yet").
 * - `unsupported_combination`: every name exists, but a measure can't be
 *   sliced or filtered that way, or two time dimensions were asked for.
 * - `invalid_range`: the range is malformed or longer than a year.
 * - `invalid_query`: any other shape problem.
 * - `query_failed`: the query was valid but the server couldn't answer it.
 */
export const CASH_QUERY_ERROR_CODES = [
  "not_in_catalog",
  "unsupported_combination",
  "invalid_range",
  "invalid_query",
  "query_failed",
] as const;
export const CashQueryErrorCodeSchema = z.enum(CASH_QUERY_ERROR_CODES);
export type CashQueryErrorCode = z.infer<typeof CashQueryErrorCodeSchema>;

export const CashQueryErrorSchema = z.object({
  code: CashQueryErrorCodeSchema,
  message: z.string(),
  /** Where in the query the problem is, e.g. ["measures", 0]. */
  path: z.array(z.union([z.string(), z.number()])).optional(),
});
export type CashQueryError = z.infer<typeof CashQueryErrorSchema>;

export const CashQueryOutcomeSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true), result: CashQueryResultSchema }),
  z.object({ ok: z.literal(false), error: CashQueryErrorSchema }),
]);
export type CashQueryOutcome = z.infer<typeof CashQueryOutcomeSchema>;

// ── Validation ─────────────────────────────────────────────────────────────

function isTimeDimension(dimension: CashDimension): boolean {
  return (CASH_TIME_DIMENSIONS as readonly CashDimension[]).includes(dimension);
}

function activeFilterKeys(filters: CashFilters): CashFilterKey[] {
  return CASH_FILTER_KEYS.filter((key) => filters[key] != null);
}

/**
 * Problems a schema-valid query can still have: a measure sliced or filtered
 * a way it doesn't support, or more than one time dimension.
 */
export function cashQueryCombinationError(
  query: CashQuery,
): CashQueryError | null {
  const filterKeys = activeFilterKeys(query.filters);
  if (query.kind === "list") {
    const allowed: readonly CashFilterKey[] =
      CASH_LIST_DEFINITIONS[query.list].filters;
    const bad = filterKeys.find((key) => !allowed.includes(key));
    return bad == null
      ? null
      : {
          code: "unsupported_combination",
          message: `List "${query.list}" can't be filtered by ${bad}.`,
          path: ["filters", bad],
        };
  }
  if (query.dimensions.filter(isTimeDimension).length > 1) {
    return {
      code: "unsupported_combination",
      message: "Use at most one of day, week and month.",
      path: ["dimensions"],
    };
  }
  for (const measure of query.measures) {
    const definition: CashMeasureDefinition = CASH_MEASURE_DEFINITIONS[measure];
    const dimension = query.dimensions.find(
      (key) => !definition.dimensions.includes(key),
    );
    if (dimension != null) {
      return {
        code: "unsupported_combination",
        message: `Measure "${measure}" can't be sliced by ${dimension}.`,
        path: ["dimensions", query.dimensions.indexOf(dimension)],
      };
    }
    const filter = filterKeys.find((key) => !definition.filters.includes(key));
    if (filter != null) {
      return {
        code: "unsupported_combination",
        message: `Measure "${measure}" can't be filtered by ${filter}.`,
        path: ["filters", filter],
      };
    }
  }
  return null;
}

const CATALOG_PATHS = new Set(["measures", "dimensions", "list", "kind"]);

function issueCode(issue: z.core.$ZodIssue): CashQueryErrorCode {
  const [head] = issue.path;
  if (head === "range") return "invalid_range";
  if (issue.code === "unrecognized_keys" && head === "filters") {
    return "not_in_catalog";
  }
  if (
    (issue.code === "invalid_value" || issue.code === "invalid_union") &&
    typeof head === "string" &&
    CATALOG_PATHS.has(head)
  ) {
    return "not_in_catalog";
  }
  return "invalid_query";
}

function valueAt(raw: unknown, path: readonly (string | number)[]): unknown {
  let value = raw;
  for (const part of path) {
    if (value == null || typeof value !== "object") return undefined;
    value = (value as Record<string | number, unknown>)[part];
  }
  return value;
}

function notInCatalogMessage(
  raw: unknown,
  path: readonly (string | number)[],
  issue: z.core.$ZodIssue | undefined,
): string {
  const names =
    issue?.code === "unrecognized_keys"
      ? issue.keys
      : [JSON.stringify(valueAt(raw, path))];
  const where = path.length ? path.join(".") : "query";
  return `Not available yet: ${names.join(", ")} (at ${where}) is not in the cash query catalog.`;
}

/**
 * Parse a query as written (a saved widget, Nami's tool call) into a
 * validated one, or the first typed error. The server runs exactly this; the
 * JSON editor can run it as you type.
 */
export function parseCashQuery(
  raw: unknown,
): { ok: true; query: CashQuery } | { ok: false; error: CashQueryError } {
  const parsed = CashQuerySchema.safeParse(raw);
  if (!parsed.success) {
    const [issue] = parsed.error.issues;
    const path = issue?.path.filter(
      (part): part is string | number => typeof part !== "symbol",
    );
    const code = issue ? issueCode(issue) : "invalid_query";
    return {
      ok: false,
      error: {
        code,
        message:
          code === "not_in_catalog" && path
            ? notInCatalogMessage(raw, path, issue)
            : z.prettifyError(parsed.error),
        path,
      },
    };
  }
  const combination = cashQueryCombinationError(parsed.data);
  return combination == null
    ? { ok: true, query: parsed.data }
    : { ok: false, error: combination };
}

/**
 * The catalog as plain text, one line per entry, for an agent tool
 * description or a help panel.
 */
export function describeCashCatalog(): string {
  const measures = CASH_MEASURES.map((id) => {
    const m: CashMeasureDefinition = CASH_MEASURE_DEFINITIONS[id];
    const slices = m.dimensions.length ? m.dimensions.join(", ") : "none";
    return `- ${id} (${m.unit}): ${m.label}. ${m.description} Dimensions: ${slices}.`;
  });
  const dimensions = CASH_DIMENSIONS.map((id) => {
    const d: CashDimensionDefinition = CASH_DIMENSION_DEFINITIONS[id];
    const values = d.values
      ? ` Values: ${d.values.map((v) => (v == null ? "null" : v)).join(", ")}.`
      : "";
    return `- ${id}: ${d.label}. ${d.description}${values}`;
  });
  const lists = CASH_LISTS.map((id) => {
    const l: CashListDefinition = CASH_LIST_DEFINITIONS[id];
    return `- ${id}: ${l.label}. ${l.description}`;
  });
  return [
    "Measures:",
    ...measures,
    "",
    "Dimensions:",
    ...dimensions,
    "",
    "Lists:",
    ...lists,
  ].join("\n");
}
